import en from "./messages/en.json";
import { DEFAULT_LOCALE, loadCatalog } from "./locales.js";

export const fallbackMessages = en;

const warned = new Set();

/** Warn once per missing key so the console stays readable. */
export function warnMissing(locale, key, warn = console.warn) {
  const id = `${locale}:${key}`;
  if (warned.has(id)) return;
  warned.add(id);
  warn(`[i18n] Missing "${locale}" translation for key "${key}"; using English.`);
}

/**
 * Merge a locale catalog over the English fallback. Every key that exists in
 * English but not in the locale is reported through `warn`.
 */
export function buildMessages(locale, catalog, fallback = fallbackMessages, warn = console.warn) {
  if (locale === DEFAULT_LOCALE || !catalog) {
    if (locale !== DEFAULT_LOCALE && !catalog) {
      warn(`[i18n] No catalog for "${locale}"; using English.`);
    }
    return { ...fallback };
  }
  const merged = { ...fallback };
  for (const key of Object.keys(fallback)) {
    if (typeof catalog[key] === "string" && catalog[key].length > 0) {
      merged[key] = catalog[key];
    } else {
      warnMissing(locale, key, warn);
    }
  }
  return merged;
}

export async function getMessages(locale) {
  if (locale === DEFAULT_LOCALE) return buildMessages(locale, null);
  const catalog = await loadCatalog(locale);
  return buildMessages(locale, catalog);
}

/** react-intl onError: surface missing ids, silence the rest of the noise in prod. */
export function handleIntlError(err, warn = console.warn) {
  if (err && err.code === "MISSING_TRANSLATION") {
    const key = err.descriptor?.id ?? "unknown";
    warnMissing("*", key, warn);
    return;
  }
  warn(err);
}
